import ApiService from 'api-client/ApiService'

export interface ResourceClasses {
  name: string
  links: Array<any>
}

export interface ProviderSummary {
  resources: Record<string, { capacity: number; used: number }>
  parent_provider_uuid: string | null
  root_provider_uuid: string
}

export interface AllocationCandidatesResult {
  providerSummaries: Record<string, ProviderSummary>
  uuidToHostname: Record<string, string>
}

class Placement extends ApiService {
  public getClassName() {
    return 'placement'
  }

  protected async getEndpoint() {
    return this.client.keystone.getServiceEndpoint('placement', 'public')
  }

  v1 = () => `/v1.10`

  async getApis() {
    const response = await this.client.basicGet<any>({
      url: '/',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackPlacementApis',
      },
    })
    return response
  }

  async getResources() {
    const response = await this.client.basicGet<any>({
      url: `/resource_providers`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getResources',
      },
    })
    return response?.resource_providers
  }

  async getResourceClasses() {
    const response = await this.client.basicGet<{
      resource_classes: ResourceClasses[]
    }>({
      url: `/resource_classes`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getResourceClasses',
        config: {
          headers: {
            'OpenStack-API-Version': 'placement 1.29',
          },
        },
      },
    })
    return response?.resource_classes || []
  }

  async getAllocationCandidates(): Promise<AllocationCandidatesResult> {
    try {
      const [resourceProvidersResponse, candidatesResponse] = await Promise.all([
        this.client.basicGet<{
          resource_providers: Array<{ uuid: string; name: string }>
        }>({
          url: `/resource_providers`,
          options: {
            clsName: this.getClassName(),
            mthdName: 'getResourceProvidersForCandidates',
          },
        }),
        this.client.basicGet<{
          allocation_requests: unknown[]
          provider_summaries: Record<string, ProviderSummary>
        }>({
          url: `/allocation_candidates?resources=VCPU:1,MEMORY_MB:1&limit=10000`,
          options: {
            clsName: this.getClassName(),
            mthdName: 'getAllocationCandidates',
            config: {
              headers: {
                'OpenStack-API-Version': 'placement 1.29',
              },
            },
          },
        }),
      ])

      const uuidToHostname = (resourceProvidersResponse?.resource_providers ?? []).reduce(
        (acc, p) => {
          acc[p.uuid] = p.name
          return acc
        },
        {} as Record<string, string>,
      )

      return {
        providerSummaries: candidatesResponse?.provider_summaries ?? {},
        uuidToHostname,
      }
    } catch {
      return { providerSummaries: {}, uuidToHostname: {} } as AllocationCandidatesResult
    }
  }

  async getResourceInventories({ id }) {
    const response = await this.client.basicGet<any>({
      url: `/resource_providers/${id}/inventories`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getResourceProvidersInventories',
      },
    })
    return response
  }

  async deleteResourceInventory(id) {
    return this.client.basicDelete<any>({
      url: `/resource_providers/${id}/inventories`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteResourceProviderInventory',
      },
    })
  }

  async updateResourceInventory({ id, body }) {
    const url = `/resource_providers/${id}/inventories`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackResourceInventory',
      },
    })
    return response
  }

  async getTraits() {
    const response = await this.client.basicGet<any>({
      url: `/traits`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getTraits',
        config: {
          headers: {
            'OpenStack-API-Version': 'placement 1.6',
          },
        },
      },
    })
    return response
  }

  async updateTrait(trait) {
    const response = await this.client.basicPut<any>({
      url: `/traits/${trait}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateTrait',
        config: {
          headers: {
            'OpenStack-API-Version': 'placement 1.6',
          },
        },
      },
    })
    return response
  }

  async getResourceTraits({ id }) {
    const response = await this.client.basicGet<any>({
      url: `/resource_providers/${id}/traits`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getResourceProviderTraits',
        config: {
          headers: {
            'OpenStack-API-Version': 'placement 1.6',
          },
        },
      },
    })
    return response
  }

  async updateResourceTraits({ id, body }) {
    const response = await this.client.basicPut<any>({
      url: `/resource_providers/${id}/traits`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateResourceProviderTraits',
        config: {
          headers: {
            'OpenStack-API-Version': 'placement 1.6',
          },
        },
      },
    })
    return response
  }
}

export default Placement
